import {
  SpacerBuilderSettingsTableRow,
  SpacerDepthType,
  SpacerTypeOption,
} from '@warebee/shared/data-access-layout-import-converter';
import classNames from 'classnames';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { Button } from '../../../../components/actions/Button';
import DropdownSelector from '../../../../components/actions/DropdownSelector';
import InputNumber from '../../../../components/inputs/InputNumber';
import { Container } from '../../../../components/layout/ContainerFlex';
import { ActionBar } from '../../../../components/nav/ActionBar';
import Tag from '../../../../helpContext/Tag';
import { converterAllAreasBuilder } from '../store/converter.state';
import {
  getSpacerDepthTypeOptions,
  getSpacerTypeOptionMap,
} from '../store/converter.types';

export type ConverterUpdateSpacerTableActionProps = {
  selectedSpacers: SpacerBuilderSettingsTableRow[];
};

const ConverterUpdateSpacerTableAction: React.FC<
  ConverterUpdateSpacerTableActionProps
> = props => {
  const { t } = useTranslation('designer');
  // const [spacerPosition, setSpacerPosition] = useState<SpacerPositionOption>(
  //   SpacerPositionOption.Left,
  // );
  const [areaBuilders, setAreaBuilders] = useRecoilState(
    converterAllAreasBuilder,
  );
  const [spacerWidth, setSpacerWidth] = useState<number>(null);
  const [spacerDepth, setSpacerDepth] = useState<number>(null);
  const [spacerType, setSpacerType] = useState<SpacerTypeOption>(null);
  const [depthType, setDepthType] = useState<SpacerDepthType>(null);

  const spacerTypeOptions = getSpacerTypeOptionMap(t);
  const spacerTypeOptionsKeys: SpacerTypeOption[] = [
    ...spacerTypeOptions.keys(),
  ];

  const spacerDepthOptions = getSpacerDepthTypeOptions(t);

  function updateSpacers() {
    const newAreaBuilders = { ...areaBuilders };

    const affectedAreas: string[] = _(props.selectedSpacers)
      .map(s => s.areaId)
      .uniq()
      .value();

    _.forEach(affectedAreas, affectedAreaId => {
      const newSpacers = { ...newAreaBuilders[affectedAreaId].spacers };
      _(props.selectedSpacers)
        .filter(s => s.areaId === affectedAreaId)
        .forEach(s => {
          newSpacers[s.bayId] = _.map(newSpacers[s.bayId], baySpacer => {
            if (baySpacer.id === s.id) {
              return {
                ...baySpacer,
                width: _.isNil(spacerWidth) ? baySpacer.width : spacerWidth,
                type: _.isNil(spacerType) ? baySpacer.type : spacerType,
                depthType: _.isNil(depthType) ? baySpacer.depthType : depthType,
                depth: _.isNil(spacerDepth) ? baySpacer.depth : spacerDepth,
              };
            } else return baySpacer;
          });
        });

      newAreaBuilders[affectedAreaId] = {
        ...newAreaBuilders[affectedAreaId],
        spacers: newSpacers,
      };
    });
    setAreaBuilders(newAreaBuilders);
  }

  function removeSpacers() {
    const newAreaBuilders = { ...areaBuilders };

    const affectedAreas: string[] = _(props.selectedSpacers)
      .map(s => s.areaId)
      .uniq()
      .value();

    _.forEach(affectedAreas, affectedAreaId => {
      const newSpacers = { ...newAreaBuilders[affectedAreaId].spacers };
      _(props.selectedSpacers)
        .filter(s => s.areaId === affectedAreaId)
        .forEach(s => {
          newSpacers[s.bayId] = _.filter(
            newSpacers[s.bayId],
            baySpacer => baySpacer.id !== s.id,
          );
        });

      newAreaBuilders[affectedAreaId] = {
        ...newAreaBuilders[affectedAreaId],
        spacers: newSpacers,
      };
    });
    setAreaBuilders(newAreaBuilders);
  }

  const selectedSpacersCount = _.size(props.selectedSpacers);

  return (
    <Container
      fullWidth={false}
      col
      overflow
      className="bg-menu/30 border-menu relative min-h-24 min-w-[16rem] border-l"
    >
      <ActionBar sticky stickyTop className="text-xxs gap-1 p-2 py-3 uppercase">
        <Button
          label={t('Batch Update:', {
            selectedBaysCount: selectedSpacersCount,
          })}
          full
          buttonType="primary"
          buttonSize={'xs'}
          className="rounded"
          isDisabled={selectedSpacersCount === 0}
          onPress={updateSpacers}
        >
          <Tag className="text-menu-active-text bg-transparent px-0.5 text-sm">
            {selectedSpacersCount}
          </Tag>
        </Button>
        <Button
          label={t('Delete:', {
            selectedBaysCount: selectedSpacersCount,
          })}
          full
          className="rounded"
          buttonType="delete"
          buttonSize={'xs'}
          isDisabled={selectedSpacersCount === 0}
          onPress={removeSpacers}
        >
          <Tag className="text-menu-active-text bg-transparent px-0.5 text-sm">
            {selectedSpacersCount}
          </Tag>
        </Button>
      </ActionBar>
      <div className="flex flex-1 flex-col justify-items-start gap-6 p-4">
        <InputNumber
          title={t`Size`}
          className="flex-none"
          inputClassName="text-xs"
          value={spacerWidth}
          onChange={v => setSpacerWidth(v)}
          range={[0, null]}
          debounceInterval={100}
        />

        <DropdownSelector
          classNameLabel={classNames('text-xs flex-1 text-menu-text')}
          className={classNames('text-xs')}
          panelMode
          widthFull
          label={t`Type`}
          value={spacerType}
          values={[null, ...spacerTypeOptionsKeys]}
          renderValue={key =>
            !_.isNil(key) ? spacerTypeOptions.get(key) : t`Current`
          }
          onChange={v => setSpacerType(v)}
          // icon={<Icon.DesignerBaysOrder className="h-8 w-8 fill-current" />}
        />

        <DropdownSelector
          className="text-sm text-white"
          panelMode
          widthFull
          DropAlignRight
          label={t`Depth Type`}
          value={depthType}
          values={[null, ..._.keys(spacerDepthOptions)] as SpacerDepthType[]}
          renderValue={key =>
            !_.isNil(key) ? spacerDepthOptions[key] : t`Current`
          }
          onChange={v => {
            setDepthType(v);
          }}
          // icon={<Icon.DesignerBaysOrder className="h-8 w-8 fill-current" />}
        />

        <InputNumber
          title={t`Depth`}
          // unitOfMeasure={sizeMeasureValue}
          value={spacerDepth}
          onChange={v => setSpacerDepth(v)}
          range={[0, null]}
          // icon={<Icon.ObjectAisleWidth className="h-8 w-8 fill-current" />}
        />
      </div>
    </Container>
  );
};
export default ConverterUpdateSpacerTableAction;
